import React, { useEffect, useState } from 'react';
import HTTP from '../../../config/axios';
import DataTable from 'react-data-table-component';
import { Button, Row, Col, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { dateIsToday, dateTimeDistance, getLongFormatOfDateAndTime, getParsedDate } from '../../../config/utils/dateFunctions';
import SimpleToast from '../../common/Toast';
import SimpleSpinner from "../../common/Spinner";
import MultiRadio from '../../form/MultiRadio';
import InputGroupField from '../../form/InputGroupField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faSearch } from '@fortawesome/free-solid-svg-icons';
import ImageCellForDataTable from '../../common/ImageCellForDataTable';
import { downloadFileFromApi, priceFormat } from '../../../config/utils/helper';
import { encryptLocalStorage } from '../../../config/utils/storage';
import DropDownField from '../../form/DropDownField';


const  TransactionDataTable : React.FC<any> = () => {

    const [sortDetails, setSortDetails] = useState({ sortColumn: '', sortDirection: '' })
    const { control, watch, getValues, reset } = useForm<Record<string, any>>({ defaultValues: { search: "", contactStatus: "All",dealership:"" } });
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [isLoading, setIsLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [isChecked, setIschecked] = useState(false);
    const [transactionData, setTransactionData] = useState([]);
    const searchText = watch('search');
    const [toast, setToast] = useState({ open: false, header: '', message: '', variant: '' })
    const [userRoleWeight, setUserRoleWeight] = useState(0);
    const selectedDealership = watch('dealership');
    const [dealershipData, setDealershipData] = useState([])
    const [defaultDealership, setDefaultDealership] = useState('');
    

    const customStyles = {
        headCells: {
            style: {
                fontSize: "15px",
                fontWeight: "700"
            },
        },
    };
    


    const column: any = [
        {
            name: 'Order Id',
            selector: (row: { orderNo: string; }) => `${row.orderNo}`,
            keyName: '',
        },
        {
            name: 'Name',
            selector: (row: { firstName: string; lastName:string }) => `${row.firstName} ${row.lastName || ''}`,
            sortable: true,
            keyName: 'firstName',
        }, 
        {
            name: 'Contact no',
            selector: (row: { phone: string; }) => row.phone,
            keyName: 'mobileNo',
        }, {
            name: 'Vehicle',
            selector: (row: { makeName:string, modelName:string, variantName:string }) => {return(`${row.makeName} ${row.modelName} ${row.variantName}`)},
            keyName: 'makeName',
            wrap: true,
        },{
            name: 'Registration',
            selector: (row: {registration:string}) => row.registration
        },
        {
            name: <div>Transaction Message</div>,
            selector: (row: {transactionMessage:string}) => row.transactionMessage || 'failed'
        },
        {
            name: <div>Bank Transaction Id</div>,
            selector: (row: {BankTransactionID:string}) => row.BankTransactionID
        },
        {
            name: <div>Bank Transaction Time</div>,
            wrap: true,
            selector: (row: { bankTransactionTime: string }) => row.bankTransactionTime ? getLongFormatOfDateAndTime(getParsedDate(row.bankTransactionTime).toString()):''
        },
        {
            name: <div>Transaction Amount</div>,
            selector: ({paymentAmount}: { paymentAmount:string;}) => `${priceFormat(paymentAmount,'en-IN',{style: 'currency',currency: 'INR',maximumFractionDigits: 2})}`,
        }
        ,{
            name: 'Transaction At',
            sortable: true,
            wrap: true,
            selector: (row: { createdAt: string; }) => dateIsToday(row.createdAt) ? dateTimeDistance(row.createdAt)
                : getLongFormatOfDateAndTime(row.createdAt),
        },
    ]


    useEffect(() => {
        const user = encryptLocalStorage.getItem('userInfo');
        const accessibleDealership = encryptLocalStorage.getItem('accessibleDealership');
        const dealershipValues = [...new Set(accessibleDealership)];
        setUserRoleWeight(user?.roleWeight || 0);
        
        // For users below roleWeight 90, set default dealership
        if (user?.roleWeight < 90 && dealershipValues.length > 0) {
            const defaultValue = String(dealershipValues[0]); // First dealership ID
            setDefaultDealership(defaultValue);
            // Set the form value
            reset({ dealership: defaultValue });
        }
    }, []);

    useEffect(() => {
        try {
            const getAllDealerships = async () => {
                try {
                    setIsLoading(true)
                    const dealershipStatus = 1
                    const params = { status: dealershipStatus };
                    const response = await HTTP.get(`/api/admin/dealership/fetch-all`, { params });
                    const { data: { status, data, msg, error, totalRecords } } = response;
                    const dealershipOptions = data.map((dealership: any) => ({
                        keyName: dealership.id,
                        name: dealership.name
                    }));
                    setDealershipData(dealershipOptions);
                } catch (error: any) {
                    if (error.response?.status === 401) {
                        setToast({ open: true, header: 'Access Denied', message: 'You do not have permission to access this resource', variant: 'danger' })
                    } else {
                        setToast({ open: true, header: 'Failed', message: 'Something went wrong', variant: 'danger' })
                    }
                }
            }
            getAllDealerships()
        }
        catch (error) {
            console.log("Error in dealership fetch api")
        }
        finally {
            setIsLoading(false)
        }
    }, []);


    useEffect(() => {
        try {
            const getAllTransactionDataData = async () => {
                try {
                    const accessibleOutlets = encryptLocalStorage.getItem("accessibleOutlets")
                    
                    setIsLoading(true);
                    const dealershipId = selectedDealership || defaultDealership;
                    const params:any = {
                        searchText: searchText, sortDetails: JSON.stringify(sortDetails),
                        ...(dealershipId ?
                            { dealershipId: dealershipId } :
                            { accessibleOutlets: accessibleOutlets })
                    };
                    // if (accessibleOutlets && accessibleOutlets.length > 0) {
                    //     params.accessibleOutlets = accessibleOutlets;
                    // }
                    const response = await HTTP.get(`/api/admin/transaction/fetch-all/${currentPage}/${perPage}`, { params });
                    const { data: { status, data, msg, error, totalRecords } } = response;
                    setTransactionData(data.rows)
                    setTotalRows(data.count)
                } catch (error: any) {
                    if (error.response?.status === 401) {
                        setToast({ open: true, header: 'Access Denied', message: 'You do not have permission to access this resource', variant: 'danger' })
                    } else {
                        setToast({ open: true, header: 'Failed', message: 'Something went wrong', variant: 'danger' })
                    }
                }
            }
            getAllTransactionDataData()
        }
        catch (e) {
            setToast({ open: true, header: 'Failed', message: 'Something went wrong', variant: 'danger' })
        }
        finally {
            setIsLoading(false)
        }

    }, [currentPage, perPage, searchText, sortDetails, selectedDealership, defaultDealership, userRoleWeight]);




    //Handling the page changes
    const handlePageChange = (page: any) => {
        setCurrentPage(page)
    }

    //handling the no.of rows changing
    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage)
    };


    //Handling the sort
    const handleSort = (column: any, sortDirection: any) => {
        const columnDetails = { sortColumn: column.keyName, sortDirection: sortDirection }
        setSortDetails(columnDetails)
    }

    //Function to download CSV
    const getAllTransactionExcel = async () => {
        try {
            const accessibleOutlets = encryptLocalStorage.getItem("accessibleOutlets")
            
            setIsLoading(true);
            const dealershipId = selectedDealership || defaultDealership;
            const params:any = {
                searchText: searchText, sortDetails: JSON.stringify(sortDetails),
                ...(dealershipId ?
                    { dealershipId: dealershipId } :
                    { accessibleOutlets: accessibleOutlets })
            };
            // if (accessibleOutlets && accessibleOutlets.length > 0) {
            //     params.accessibleOutlets = accessibleOutlets;
            // }
            const response = await HTTP.get(`/api/admin/transaction/fetch-all/excel`, { params, responseType: 'blob', });
            const filename = response.headers['content-disposition'].split('filename="')[1].split('"')[0];
            downloadFileFromApi(response.data, filename)
        } catch (error: any) {
            if (error.response?.status === 401) {
                setToast({ open: true, header: 'Access Denied', message: 'You do not have permission to access this resource', variant: 'danger' })
            } else {
                console.log("Error in enquery download api", error);
            }
        } finally {
            setIsLoading(false);
        }
    }

    return (<>
        <Row className='mb-1'>
            <div className="d-flex">
                <div className="p-2 flex-grow-1"><h2 className="mb-0 text-gray">All&nbsp;Transactions</h2></div>
                <div className='p-2 mt-2'>
                    <label className="form-label mb-0 me-0 fw-semibold text-nowrap">Dealership:</label>
                </div>
                <div className='p-2'>
                    <div className="ms-auto" style={{ width: '100%' }}>
                        <DropDownField
                            name="dealership"
                            options={dealershipData}
                            control={control}
                            placeholder="Choose Dealership"
                            isValueKeyname={true}
                            isDisabled={userRoleWeight < 90}
                        />
                    </div>
                </div>
                <div className="p-2 mt-2 text-success" title="Download Excel Report">
                    <FontAwesomeIcon icon={faFileExcel} size='lg' onClick={getAllTransactionExcel} style={{ cursor: 'pointer' }} />
                </div>
                <div className="p-2"><InputGroupField name="search" placeholder="Search" label="" control={control} addOn={faSearch} /></div>
            </div>
        </Row>

        <DataTable
            columns={column}
            data={transactionData}
            striped={true}
            //progressPending={isLoading}
            onSort={handleSort}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            highlightOnHover={true}
            customStyles={customStyles}
        />
        <SimpleToast show={toast.open} header={toast.header}
            message={toast.message} variant={toast.variant}
            onClose={() => setToast({ ...toast, open: false })} />
        <SimpleSpinner show={isLoading} />

    </>)
}

export default TransactionDataTable